<template>
  <div class="notable-detail cs-scroll-style">
    <div v-if="isMobile" class="mobile-back">
      <BackButton :clickMethod="closeDetail" />
    </div>

    <SpinnerContainer v-if="$store.state.appConfig.isLoading" />
    <template v-if="detail.id && !$store.state.appConfig.isLoading">
      <div class="detail-content-container">
      <!-- Video -->
      <div class="video-wrapper" v-if="detail.videoUrl">
        <video
          ref="videoRef"
          playsinline
          preload="auto"
          @loadedmetadata="onVideoMetadataLoaded"
          @ended="isPlaying = false"
        >
          <source :src="detail.videoUrl" type="video/mp4" />
        </video>
        <div class="video-overlay">
          <!-- Left: title + play controls -->
          <div class="overlay-left">
            <span class="video-title">{{ detail.title }}</span>
            <div class="overlay-left-controls">
              <button class="ctrl-btn" @click="togglePlay" :aria-label="isPlaying ? 'Pause' : 'Play'">
                <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="white" width="26" height="26">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="white" width="26" height="26">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              </button>
              <button class="ctrl-btn" @click="enterFullscreen" aria-label="Fullscreen">
                <svg viewBox="0 0 24 24" fill="white" width="26" height="26">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Right: volume -->
          <div class="overlay-right">
            <button class="ctrl-btn" @click="toggleMute" :aria-label="isMuted ? 'Unmute' : 'Mute'">
              <!-- muted / volume 0 -->
              <svg v-if="isMuted || volume === 0" viewBox="0 0 24 24" fill="white" width="26" height="26">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              </svg>
              <!-- low volume -->
              <svg v-else-if="volume < 0.5" viewBox="0 0 24 24" fill="white" width="26" height="26">
                <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
              </svg>
              <!-- high volume -->
              <svg v-else viewBox="0 0 24 24" fill="white" width="26" height="26">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            </button>
            <input
              type="range"
              class="volume-slider"
              min="0"
              max="1"
              step="0.05"
              :value="isMuted ? 0 : volume"
              :style="{ '--vol': (isMuted ? 0 : volume * 100) + '%' }"
              @input="setVolume"
              aria-label="Volume"
            />
          </div>
        </div>
      </div>

      <!-- Description + images interleaved -->
      <div class="content-body">
        <div class="description-section">
          <h5>{{ $tI('Description title') }}</h5>
          <hr />

          <p v-if="detail.descriptionP1" class="desc-paragraph" v-html="formattedDescriptionP1"></p>

          <figure v-if="detail.imageSlot1" class="content-figure">
            <div class="figure-img-bg">
              <img :src="detail.imageSlot1" :alt="formattedCaption1" />
            </div>
            <figcaption v-if="imageMeta.caption1">{{ formattedCaption1 }}</figcaption>
            <figcaption v-if="imageMeta.attribution1">{{ imageMeta.attribution1 }}</figcaption>
          </figure>

          <p v-if="detail.descriptionP2" class="desc-paragraph" v-html="formattedDescriptionP2"></p>

          <figure v-if="detail.imageSlot2" class="content-figure">
            <div class="figure-img-bg">
              <img :src="detail.imageSlot2" :alt="formattedCaption2" />
            </div>
            <figcaption v-if="imageMeta.caption2">{{ formattedCaption2 }}</figcaption>
            <figcaption v-if="imageMeta.attribution2">{{ imageMeta.attribution2 }}</figcaption>
          </figure>

          <p v-if="detail.descriptionP3" class="desc-paragraph" v-html="formattedDescriptionP3"></p>

          <figure v-if="detail.imageSlot3" class="content-figure">
            <div class="figure-img-bg">
              <img :src="detail.imageSlot3" :alt="formattedCaption3" />
            </div>
            <figcaption v-if="imageMeta.caption3">{{ formattedCaption3 }}</figcaption>
            <figcaption v-if="imageMeta.attribution3">{{ imageMeta.attribution3 }}</figcaption>
          </figure>
        </div>

        <!-- Modern population distance table -->
        <div class="distance-section" v-if="distanceList && distanceList.length">
          <table class="table distance-table">
            <thead class="table-light">
              <tr>
                <th>{{ $tI('Modern population title') }}</th>
                <th class="text-right">{{ $tI('Distance title') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in distanceList" :key="row.title">
                <td>{{ $t(row.title.replaceAll('*', ' ')) }}</td>
                <td class="text-right"><span :style="{ color: row.gradHSL }">{{ row.distance }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Source / Meta -->
        <div class="source-section">
          <h5>{{ $tI('Source') }}</h5>
          <hr />
          <dl class="meta-list">
            <div class="meta-row" v-if="tData">
              <dt>{{ $tI('Date title') }}:</dt>
              <dd>{{ tData }}</dd>
            </div>
            <div class="meta-row" v-if="detail.yDna">
              <dt>{{ $tI('Y-DNA') }}:</dt>
              <dd>{{ detail.yDna }}</dd>
            </div>
            <div class="meta-row" v-if="detail.mtDna">
              <dt>{{ $tI('mtDNA') }}:</dt>
              <dd>{{ detail.mtDna }}</dd>
            </div>
            <div class="meta-row" v-if="detail.location">
              <dt>{{ $tI('Location title') }}:</dt>
              <dd>{{ $t(detail.location) }}</dd>
            </div>
            <div class="meta-row" v-if="researchHtml">
              <dt>{{ $tI('Research title') }}:</dt>
              <dd>{{ researchHtml }}</dd>
            </div>
            <div class="meta-row" v-if="detail.samples">
              <dt>{{ $tI('Sample title') }}:</dt>
              <dd>{{ detail.samples }}</dd>
            </div>
          </dl>
        </div>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
import BackButton from '@/components/ui/BackButton.vue';
import notableProximityDistanceWatcher from '../services/notableProximityDistanceWatcher';
import { tDateParser } from '@/services/Base/utils';

export default {
  components: {
    BackButton,
  },
  data() {
    return {
      isPlaying: false,
      isMuted: false,
      volume: 0.5,
    };
  },
  computed: {
    detail() {
      return this.$store.state.ancientNotableIndividualResult.openedDetail;
    },
    imageMeta() {
      try {
        return JSON.parse(this.detail.imageMetadata || '{}');
      } catch {
        return {};
      }
    },
    formattedDescriptionP1() {
      const text = this.$tPop(this.detail.code, 'descriptionP1', this.detail.descriptionP1);
      return text ? text.replace(/\n/g, '<br>') : '';
    },
    formattedDescriptionP2() {
      const text = this.$tPop(this.detail.code, 'descriptionP2', this.detail.descriptionP2);
      return text ? text.replace(/\n/g, '<br>') : '';
    },
    formattedDescriptionP3() {
      const text = this.$tPop(this.detail.code, 'descriptionP3', this.detail.descriptionP3);
      return text ? text.replace(/\n/g, '<br>') : '';
    },
    formattedCaption1() {
      return this.$tPop(this.detail.code, 'imageCaption1', this.imageMeta.caption1);
    },
    formattedCaption2() {
      return this.$tPop(this.detail.code, 'imageCaption2', this.imageMeta.caption2);
    },
    formattedCaption3() {
      return this.$tPop(this.detail.code, 'imageCaption3', this.imageMeta.caption3);
    },
    distanceList() {
      return this.$store.state.ancientNotableIndividualResult.notableProximityResult;
    },
    researchHtml() {
      const research = this.detail.research;
      if (!research) return null;
      const pattern = /<\/?[a-z][\s\S]*>/i;
      if (pattern.test(research)) {
        return research.replace(/<[^>]*>/g, '');
      } else if (research.includes('*')) {
        return research.split(' ')
          .map(res => res.split('#')[0].replaceAll('*', ' '))
          .join(', ');
      } else {
        return research.split('\n')
          .filter(Boolean)
          .join(', ');
      }
    },
    tData() {
      const date = this.detail.date;
      if (!date) return null;
      const dateObj = tDateParser(date);
      return this.$tParams(dateObj.key, dateObj.params, dateObj.value, date);
    },
    isMobile() {
      return this.$store.state.appConfig.windowWidth < 769;
    },
    isMobileApp() {
      return this.$store.state.appConfig.is_embedded_in_mh_app;
    },
  },
  watch: {
    'detail.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        this.resetVideo();
      }
    },
  },
  methods: {
    onVideoMetadataLoaded() {
      const video = this.$refs.videoRef;
      if (!video) return;
      video.currentTime = 0;
      video.volume = this.volume;
    },
    closeDetail() {
      this.$store.state.ancientNotableIndividualResult.isOpenedDetail = false;
    },
    togglePlay() {
      const video = this.$refs.videoRef;
      if (!video) return;
      if (video.paused) {
        video.play();
        this.isPlaying = true;
      } else {
        video.pause();
        this.isPlaying = false;
      }
    },
    toggleMute() {
      const video = this.$refs.videoRef;
      if (!video) return;
      this.isMuted = !this.isMuted;
      video.muted = this.isMuted;
      if (!this.isMuted) {
        video.volume = this.volume;
      }
    },
    setVolume(e) {
      const video = this.$refs.videoRef;
      if (!video) return;
      this.volume = parseFloat(e.target.value);
      if (this.volume === 0) {
        this.isMuted = true;
        video.muted = true;
      } else {
        if (this.isMuted) {
          this.isMuted = false;
          video.muted = false;
        }
        video.volume = this.volume;
      }
    },
    enterFullscreen() {
      const video = this.$refs.videoRef;
      if (!video) return;
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      }
    },
    resetVideo() {
      this.isPlaying = false;
      this.$nextTick(() => {
        const video = this.$refs.videoRef;
        if (!video) return;
        video.load();
        video.play().then(() => {
          this.isPlaying = true;
        }).catch(() => {
          this.isPlaying = false;
        });
      });
    },
  },
  mounted() {
    notableProximityDistanceWatcher.init();
    this.$nextTick(() => {
      const video = this.$refs.videoRef;
      if (!video) return;
      video.play().then(() => {
        this.isPlaying = true;
      }).catch(() => {
        this.isPlaying = false;
      });
    });
  },
  beforeDestroy() {
    notableProximityDistanceWatcher.cleanup();
  },
};
</script>

<style lang="scss" scoped>
.notable-detail {
  height: 100vh;
  overflow-y: auto;
  background-color: var(--white-100);
}

.mobile-back {
  padding: 16px 16px 10px;
}

.detail-content-container {
  max-width: 850px;
  margin: 0 auto;
}

/* ── Video ── */
.video-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  overflow: hidden;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
    display: block;
  }

  .video-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1.5rem 1rem 0.85rem;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.72));
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    color: #fff;
  }

  .overlay-left {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .video-title {
    font-size: 1rem;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  }

  .overlay-left-controls {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .overlay-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .ctrl-btn {
    background: rgba(0, 0, 0, 0.45);
    border: none;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    opacity: 0.9;

    &:hover {
      opacity: 1;
      background: rgba(0, 0, 0, 0.65);
    }
  }

  .volume-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 0;
    overflow: hidden;
    transition: width 0.25s ease;
    height: 4px;
    border-radius: 2px;
    background: linear-gradient(
      to right,
      #fff var(--vol, 50%),
      rgba(255, 255, 255, 0.35) var(--vol, 50%)
    );
    outline: none;
    cursor: pointer;
    align-self: center;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 13px;
      height: 13px;
      border-radius: 50%;
      background: #fff;
      cursor: pointer;
      transform: scale(0);
      transition: transform 0.15s ease;
    }

    &::-moz-range-thumb {
      width: 13px;
      height: 13px;
      border-radius: 50%;
      background: #fff;
      cursor: pointer;
      border: none;
      transform: scale(0);
      transition: transform 0.15s ease;
    }
  }

  .overlay-right:hover .volume-slider {
    width: 75px;

    &::-webkit-slider-thumb {
      transform: scale(1);
    }

    &::-moz-range-thumb {
      transform: scale(1);
    }
  }

  @media (max-width: 768px) {
    aspect-ratio: unset;
    height: 220px;
  }
}

.content-body {
  padding: 24px 28px 40px;
}

.description-section {
  margin-bottom: 28px;

  h5 {
    margin-bottom: 4px;
    font-weight: bold;
  }
}

.desc-paragraph {
  font-size: 0.93rem;
  line-height: 1.6;
  color: var(--txt-p-description-color, var(--black-40));
  margin-bottom: 16px;
  white-space: pre-line;
}

.distance-section {
  margin-bottom: 28px;
  padding: 0 16px;

  @media (max-width: 768px) {
    padding: 0;
  }
}

.source-section {
  h5 {
    margin-bottom: 4px;
    font-weight: bold;
  }
}

.content-figure {
  margin: 20px 0;
  text-align: center;

  .figure-img-bg {
    background-color: #F7F7F7;
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  img {
    max-width: 100%;
    max-height: 380px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
    display: block;
  }

  figcaption {
    margin-top: 8px;
    font-size: 0.8rem;
    color: var(--black-40);
    font-style: italic;
  }

  @media (max-width: 768px) {
    img {
      max-height: 260px;
    }
  }
}

.distance-table {
  border: 1px solid var(--border-color, #dee2e6);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;

  thead th {
    font-weight: 700;
    font-size: 0.875rem;
    padding: 12px 16px;
    background-color: #f8f9fa;
    border-bottom: 1px solid var(--border-color, #dee2e6);
    border-top: none;
    border-left: none;
    border-right: none;

    &:last-child {
      width: 90px;
    }
  }

  tbody tr {
    &:first-child td {
      border-top: none;
    }
  }

  tbody td {
    padding: 10px 16px;
    font-size: 0.875rem;
    vertical-align: middle;
    border: none;
    border-top: 1px solid var(--border-color, #dee2e6);

    span {
      font-weight: 600;
    }
  }
}

.meta-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 0.9rem;
  align-items: baseline;
  flex-wrap: wrap;

  dt {
    font-weight: 700;
    white-space: nowrap;
  }

  dd {
    margin: 0;
    word-break: break-word;
  }
}
</style>
